import React, { useState } from "react";
import { FaPlus, FaMinus, FaQuestionCircle, FaLightbulb } from "react-icons/fa";
import { Link } from "react-router-dom";
import SeoMeta from "../SeoMeta";
import { useTheme } from "../Theme/ThemeContext";

const faqs = [
  {
    question: "What is CodeArena?",
    answer:
      "CodeArena is a platform where developers can explore jobs, internships, and referrals, and get guidance to kickstart their tech careers.",
    category: "General"
  },
  {
    question: "What is the Golang Bootcamp?",
    answer:
      "Our Golang Bootcamp takes you from zero to hero with comprehensive theory, video lessons, MCQs, and coding practice with real-time test execution and hidden test case analysis.",
    category: "Courses"
  },
  {
    question: "Do you guarantee job placements?",
    answer:
      "For selected programs and opportunities, we provide direct placement support through our partner companies.",
    category: "Jobs"
  },
  {
    question: "Can I get a referral for a job?",
    answer:
      "Yes, CodeArena provides referral opportunities for eligible candidates through verified connections with industry professionals.",
    category: "Referrals"
  },
  {
    question: "What kind of roles are available?",
    answer:
      "We list a variety of roles across software development, IT, and other tech domains, including internships, entry-level jobs, and experienced positions.",
    category: "Jobs"
  },
  {
    question: "How can I track available job opportunities?",
    answer:
      "All open positions are listed on the CodeArena jobs page. You can filter opportunities by skills, experience, and location to find the best match.",
    category: "Jobs"
  },
  {
    question: "Do I need prior experience to apply for jobs?",
    answer:
      "Some roles are open to freshers, while others may require specific skills or experience. Each listing provides detailed requirements.",
    category: "Jobs"
  },
  {
    question: "Do you provide internships?",
    answer:
      "Yes, we partner with companies to offer internship opportunities for eligible candidates.",
    category: "Internships"
  },
  {
    question: "How do I apply for a job or internship?",
    answer:
      "After reviewing the listings, you can apply directly through our platform. We also provide guidance on resume preparation and interview tips.",
    category: "General"
  },
  {
    question: "Are there any skill assessments?",
    answer:
      "Certain opportunities may include coding challenges or assessments to match candidates with suitable roles.",
    category: "Courses"
  },
  {
    question: "How do referrals work?",
    answer:
      "Referrals are provided for candidates who meet eligibility criteria. This increases your chances of getting noticed by hiring companies.",
    category: "Referrals"
  },
];

const categoryColors = {
  General: "from-blue-500 to-cyan-500",
  Courses: "from-purple-500 to-pink-500",
  Jobs: "from-green-500 to-emerald-500",
  Referrals: "from-orange-500 to-red-500",
  Internships: "from-indigo-500 to-violet-500",
};

export default function FAQ() {
  const [openIndex, setOpenIndex] = useState(null);
  const { currentTheme } = useTheme();

  // FAQPage JSON-LD schema
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((faq) => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.answer,
      },
    })),
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-neutral-50 via-white to-neutral-100 dark:from-neutral-900 dark:via-neutral-900 dark:to-neutral-800">
      <SeoMeta
        title="FAQ – Code Arena"
        description="Find answers to frequently asked questions about CodeArena, the Golang bootcamp, jobs, internships, referrals, and placement support."
        url="https://www.code-arena.in/faq"
        image="/logo192.png"
        keywords="codearena faq, golang bootcamp faq, jobs faq, internships faq, placement support"
        jsonLd={faqSchema}
      />
      
      {/* Hero Section */}
      <div className={`relative bg-gradient-to-r ${currentTheme.colors.gradient} py-20 overflow-hidden`}>
        {/* Decorative elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-10 left-10 w-64 h-64 bg-white/10 rounded-full filter blur-3xl"></div>
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-emerald-400/20 rounded-full filter blur-3xl"></div>
        </div>
        
        <div className="relative z-10 max-w-4xl mx-auto px-4 text-center">
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-4 py-2 mb-6">
            <FaQuestionCircle className="w-4 h-4 text-white" />
            <span className="text-white text-sm font-medium">Help Center</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Frequently Asked Questions
          </h1>
          <p className="text-white/80 text-lg max-w-2xl mx-auto">
            Everything you need to know about CodeArena, jobs, internships, and referrals.
          </p>
        </div>
      </div>

      {/* FAQ List */}
      <div className="max-w-3xl mx-auto px-4 py-16 -mt-12 relative z-10">
        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div
              key={index}
              className={`glass rounded-2xl border border-white/20 dark:border-white/10 overflow-hidden transition-all duration-300 ${
                openIndex === index ? "shadow-lg ring-2 ring-accent-500/20" : "hover:shadow-md"
              }`}
            >
              <button
                onClick={() =>
                  setOpenIndex(openIndex === index ? null : index)
                }
                className="w-full flex justify-between items-center gap-4 px-6 py-5 text-left"
              >
                <div className="flex items-start gap-4">
                  <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${categoryColors[faq.category] || "from-gray-400 to-gray-500"} flex items-center justify-center flex-shrink-0`}>
                    <FaLightbulb className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <span className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-medium mb-1.5 bg-gradient-to-r ${categoryColors[faq.category] || "from-gray-400 to-gray-500"} text-white`}>
                      {faq.category}
                    </span>
                    <p className="font-semibold text-gray-900 dark:text-white">{faq.question}</p>
                  </div>
                </div>
                <span className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 transition-colors ${
                  openIndex === index 
                    ? `bg-gradient-to-r ${currentTheme.colors.gradient} text-white` 
                    : "bg-neutral-100 dark:bg-neutral-700 text-neutral-500 dark:text-neutral-400"
                }`}>
                  {openIndex === index ? (
                    <FaMinus className="w-3 h-3" />
                  ) : (
                    <FaPlus className="w-3 h-3" />
                  )}
                </span>
              </button>

              {/* Answer */}
              <div className={`overflow-hidden transition-all duration-300 ${
                openIndex === index ? "max-h-96" : "max-h-0"
              }`}>
                <div className="px-6 pb-5 pl-20 text-neutral-600 dark:text-neutral-300 leading-relaxed">
                  {faq.answer}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* CTA Section */}
        <div className={`mt-12 bg-gradient-to-r ${currentTheme.colors.gradient} rounded-3xl p-8 text-center relative overflow-hidden shadow-lg ${currentTheme.colors.glow}`}>
          {/* Decorative elements */}
          <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full filter blur-2xl"></div>
          <div className="absolute bottom-0 left-0 w-24 h-24 bg-emerald-400/20 rounded-full filter blur-2xl"></div>
          
          <div className="relative z-10">
            <h3 className="text-2xl font-bold text-white mb-2">Still have questions?</h3>
            <p className="text-white/80 mb-6">
              Can't find the answer you're looking for? We're here to help!
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 bg-white text-accent-600 font-semibold py-3 px-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-[1.02]"
            >
              Contact Support
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
